import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchVendors,
  createVendor,
  deleteVendor,
  toggleVendorStatus,
  toggleVendorApproval,
  toggleVendorFeatured,
  toggleVendorBestSeller,
} from "../../../slice/vendor/vendormanagementSlice";

const BASE_URL = process.env.REACT_APP_API_URL;

const FILTERS = [
  { key: "all",        label: "All" },
  { key: "approved",   label: "Approved" },
  { key: "pending",    label: "Pending" },
  { key: "suspended",  label: "Suspended" },
  { key: "featured",   label: "Featured" },
  { key: "bestSeller", label: "Best Seller" },
];

const VendorManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

const { vendors, loading, error, pagination } = useSelector((state) => state.vendormanagement);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");

  const [vendorName, setVendorName]     = useState("");
  const [uenNumber, setUenNumber]       = useState("");
  const [shopAddress, setShopAddress]   = useState("");
  const [mobile, setMobile]             = useState("");
  const [password, setPassword]         = useState("");
  const [certificate, setCertificate]   = useState(null);
  const [shopPhotos, setShopPhotos]     = useState([]);

  useEffect(() => {
    dispatch(fetchVendors({ page, limit, search: searchTerm, filter: activeFilter }));
  }, [dispatch, page, searchTerm, activeFilter]);

  const openAddModal = () => {
    setVendorName(""); setUenNumber(""); setShopAddress("");
    setMobile(""); setPassword(""); setCertificate(null); setShopPhotos([]);
    setFormError(""); setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const handleSave = async () => {
    if (!vendorName.trim() || !uenNumber.trim() || !shopAddress.trim() || !mobile.trim() || !password.trim()) {
      setFormError("All fields marked required must be filled");
      return;
    }
    setSaving(true); setFormError("");

    const formData = new FormData();
    formData.append("vendorName", vendorName.trim());
    formData.append("uenNumber", uenNumber.trim());
    formData.append("shopAddress", shopAddress.trim());
    formData.append("mobile", mobile.trim());
    formData.append("password", password.trim());
    if (certificate) formData.append("registrationCertificate", certificate);
    shopPhotos.forEach((file) => formData.append("shopPhotos", file));

    try {
      await dispatch(createVendor(formData)).unwrap();
      toast.success("Vendor invited successfully!");
      setPage(1);
      dispatch(fetchVendors({ page: 1, limit, search: searchTerm, filter: activeFilter }));
      handleClose();
    } catch (err) {
      setFormError(err || "Something went wrong");
      toast.error(err || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await dispatch(deleteVendor(id)).unwrap();
      dispatch(fetchVendors({ page, limit, search: searchTerm, filter: activeFilter }));
      toast.success("Vendor deleted successfully!");
    } catch (err) {
      toast.error(err || "Delete failed");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await dispatch(toggleVendorStatus(id)).unwrap();
      toast.success("Vendor status updated");
    } catch (err) {
      toast.error(err || "Failed to update status");
    }
  };

  const handleToggleApproval = async (id) => {
    try {
      await dispatch(toggleVendorApproval(id)).unwrap();
      toast.success("Vendor approval updated");
    } catch (err) {
      toast.error(err || "Failed to update approval");
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await dispatch(toggleVendorFeatured(id)).unwrap();
      toast.success("Featured status updated");
    } catch (err) {
      toast.error(err || "Failed to update featured status");
    }
  };

  const handleToggleBestSeller = async (id) => {
    try {
      await dispatch(toggleVendorBestSeller(id)).unwrap();
      toast.success("Best seller status updated");
    } catch (err) {
      toast.error(err || "Failed to update best seller status");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const statusInfo = (vendor) => {
    if (!vendor.status) return { label: "suspended", bg: "#fef2f2", color: "#dc2626" };
    if (!vendor.approval) return { label: "pending", bg: "#fffbeb", color: "#b45309" };
    return { label: "approved", bg: "#ecfdf5", color: "#059669" };
  };

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#111827" }}>Vendor Management</h1>
          <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 14 }}>
            {pagination.total ?? vendors.length} vendors registered
          </p>
        </div>
      </div>

      {/* ── Search + Filters ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 10,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px",
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9aa0ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            style={{ border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent" }}
          />
        </div>

        {FILTERS.map((f) => {
          const activeStyle = activeFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => { setActiveFilter(f.key); setPage(1); }}
              style={{
                padding: "12px 20px",
                borderRadius: 12,
                border: activeStyle ? "none" : "1px solid #e5e7eb",
                background: activeStyle ? "#111827" : "#fff",
                color: activeStyle ? "#fff" : "#374151",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eef0f4", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>{error}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {["VENDOR", "UEN NUMBER", "SHOP ADDRESS", "MOBILE", "REGISTERED", "STATUS", "FEATURED", "BEST SELLER", "ACTIONS"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "14px 20px", fontSize: 11,
                        fontWeight: 700, letterSpacing: 0.5, color: "#9ca3af",
                        borderBottom: "1px solid #eef0f4", whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendors.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: "50px 0", color: "#aaa" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#bbb" }}>No vendors found</span>
                        <span style={{ fontSize: 12, color: "#d1d5db" }}>
                          {searchTerm ? `No results for "${searchTerm}"` : "Invite your first vendor using the button above"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor) => {
                    const s = statusInfo(vendor);
                    const photo = vendor.shopPhotos?.[0]
                      ? `${BASE_URL}/uploads/vendors/${vendor.shopPhotos[0]}`
                      : null;
                    return (
                      <tr key={vendor._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                              width: 38, height: 38, borderRadius: 10, background: "#f3f4f6",
                              display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0,
                            }}>
                              {photo ? (
                                <img src={photo} alt={vendor.vendorName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 9l1-5h16l1 5" /><path d="M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9" />
                                  <path d="M9 21V13h6v8" />
                                </svg>
                              )}
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{vendor.vendorName}</span>
                          </div>
                        </td>
                        <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>{vendor.uenNumber}</td>
                        <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563", maxWidth: 220 }}>{vendor.shopAddress}</td>
                        <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>{vendor.mobile}</td>
                        <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>{formatDate(vendor.createdAt)}</td>
                        <td style={{ padding: "16px 20px" }}>
                          <span style={{
                            background: s.bg, color: s.color, fontSize: 12, fontWeight: 700,
                            padding: "5px 12px", borderRadius: 20, textTransform: "lowercase",
                          }}>
                            {s.label}
                          </span>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={vendor.featuredVendor || false}
                              onChange={() => handleToggleFeatured(vendor._id)}
                            />
                            <span className="slider round" />
                          </label>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={vendor.bestSellerVendor || false}
                              onChange={() => handleToggleBestSeller(vendor._id)}
                            />
                            <span className="slider round" />
                          </label>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={() => navigate(`/admin/VendorManagement/${vendor._id}`)}
                              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              View
                            </button>
                            {!vendor.approval && vendor.status && (
                              <button
                                onClick={() => handleToggleApproval(vendor._id)}
                                style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                              >
                                Approve
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleStatus(vendor._id)}
                              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: vendor.status ? "#dc2626" : "#16a34a", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              {vendor.status ? "Suspend" : "Activate"}
                            </button>
                            <button
                              onClick={() => handleDelete(vendor._id)}
                              title="Delete"
                              style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" /><path d="M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px" }}>
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer" }}
            >Prev</button>
            {Array.from({ length: pagination.totalPages }, (_, i) => (
              <button key={i + 1} onClick={() => setPage(i + 1)}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: page === i + 1 ? "none" : "1px solid #e5e7eb",
                  background: page === i + 1 ? "#111827" : "#fff",
                  color: page === i + 1 ? "#fff" : "#374151",
                  cursor: "pointer",
                }}
              >{i + 1}</button>
            ))}
            <button disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === pagination.totalPages ? "not-allowed" : "pointer" }}
            >Next</button>
          </div>
        )}
      </div>

      {/* ── INVITE VENDOR MODAL ── */}
      {showModal && (
        <>
          <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)", zIndex: 1040 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto",
            background: "#fff", borderRadius: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            padding: "32px 32px 28px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Invite Vendor</h2>
              <button onClick={handleClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", fontSize: 16, color: "#555" }}>×</button>
            </div>

            <hr style={{ borderColor: "#f0f0f0", marginBottom: 20 }} />

            {formError && (
              <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                {formError}
              </div>
            )}

            {[
              { label: "Vendor Name", value: vendorName, setter: setVendorName, placeholder: "e.g. Lotus Palace Florist" },
              { label: "UEN Number",  value: uenNumber,  setter: setUenNumber,  placeholder: "e.g. 201912345K" },
              { label: "Shop Address", value: shopAddress, setter: setShopAddress, placeholder: "e.g. 12 Serangoon Road, Little India" },
              { label: "Mobile", value: mobile, setter: setMobile, placeholder: "e.g. 91234567" },
              { label: "Temporary Password", value: password, setter: setPassword, placeholder: "Set an initial password", type: "password" },
            ].map((field) => (
              <div key={field.label} style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase" }}>
                  {field.label} <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type={field.type || "text"}
                  value={field.value}
                  onChange={(e) => field.setter(e.target.value)}
                  placeholder={field.placeholder}
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 14, boxSizing: "border-box", outline: "none" }}
                  onFocus={(e) => e.target.style.borderColor = "#6362e7"}
                  onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                />
              </div>
            ))}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase" }}>
                Registration Certificate <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span>
              </label>
              <input type="file" accept="application/pdf,image/*" onChange={(e) => setCertificate(e.target.files[0])} />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase" }}>
                Shop Photos <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional, up to 6)</span>
              </label>
              <input type="file" accept="image/*" multiple onChange={(e) => setShopPhotos(Array.from(e.target.files))} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={handleClose} style={{ padding: "11px 24px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#f9fafb", color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: "11px 24px", borderRadius: 10, border: "none", background: saving ? "#a5a4f3" : "#6362e7", color: "#fff", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Sending Invite..." : "Send Invite"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorManagement;